import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, CalendarPlus, Copy, CheckCheck, ClipboardList, CalendarClock } from "lucide-react";

const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function NotificationBell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 25000,
  });
  const { data: feed } = useQuery({
    queryKey: ["cal-feed-url"],
    queryFn: async () => (await api.get("/calendar/feed-url")).data,
    enabled: calOpen,
  });

  const markRead = useMutation({
    mutationFn: async (payload) => (await api.post("/notifications/read", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = data?.unread || 0;
  const items = data?.items || [];

  const openItem = (n) => {
    if (!n.read) markRead.mutate({ ids: [n.id] });
    setOpen(false);
    if (n.link) nav(n.link);
  };

  const copyUrl = () => {
    try { navigator.clipboard.writeText(feed?.url || ""); toast.success("Calendar link copied"); } catch { /* older browsers */ }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="notification-bell" className="relative p-2 text-slate-500 hover:text-cyan-600 transition-colors" aria-label="Notifications">
            <Bell size={19} />
            {unread > 0 && (
              <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 bg-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0 rounded-sm" data-testid="notification-popover">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="font-display font-semibold text-slate-900 text-sm">Notifications</div>
            {unread > 0 && (
              <button data-testid="mark-all-read-btn" onClick={() => markRead.mutate({ all: true })} className="text-[11px] text-cyan-600 hover:underline flex items-center gap-1">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-slate-400">No notifications yet.<br />You&apos;ll see tasks &amp; bookings assigned to you here.</div>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid="notification-item"
                className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-cyan-50/50 transition-colors flex gap-2.5 ${n.read ? "opacity-60" : ""}`}>
                <div className={`w-7 h-7 shrink-0 rounded-sm flex items-center justify-center mt-0.5 ${n.kind === "booking" ? "bg-violet-100 text-violet-700" : "bg-cyan-100 text-cyan-700"}`}>
                  {n.kind === "booking" ? <CalendarClock size={13} /> : <ClipboardList size={13} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-900 truncate">{n.title}</div>
                  {n.body && <div className="text-[11px] text-slate-500 truncate">{n.body}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-cyan-500 shrink-0 mt-1.5" />}
              </button>
            ))}
          </div>
          <button data-testid="phone-calendar-btn" onClick={() => { setOpen(false); setCalOpen(true); }}
            className="w-full px-4 py-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors flex items-center gap-1.5">
            <CalendarPlus size={13} /> Get your tasks &amp; bookings on your phone calendar
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={calOpen} onOpenChange={setCalOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="phone-calendar-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Phone calendar sync</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Subscribe to your personal CCFI feed — tasks and bookings assigned to you appear in your phone&apos;s calendar automatically and stay up to date.</p>
          <div className="flex gap-2">
            <div className="flex-1 border border-slate-200 bg-slate-50 rounded-sm p-2 font-mono text-[10px] text-slate-600 break-all" data-testid="cal-feed-url">{feed?.url || "…"}</div>
            <Button size="sm" variant="outline" onClick={copyUrl} className="rounded-sm shrink-0 self-center" data-testid="copy-feed-url-btn"><Copy size={13} /></Button>
          </div>
          <div className="text-xs text-slate-600 space-y-2 border-t border-slate-100 pt-3">
            <div><b>iPhone:</b> Settings → Apps → Calendar → Calendar Accounts → Add Account → Other → Add Subscribed Calendar → paste the link.</div>
            <div><b>Google / Android:</b> Google Calendar on the web → Other calendars ＋ → From URL → paste the link (syncs to your phone).</div>
            <div><b>Outlook:</b> Add calendar → Subscribe from web → paste the link.</div>
          </div>
          <p className="text-[10px] text-slate-400">This link is personal to you — don&apos;t share it.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
